import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { useToast } from 'use-toast-mui';
import { Table, TableBody, Tooltip, Collapse, Switch } from '@mui/material';
import CommonFilterToggle from '../../../../form-components/CommonFilterToggle';
import qs from 'qs';

import AnimationPage from '../../../../pages/AnimationPage';
import { getAllPass } from '../../../../api/ticket';
import { saleBranchLabel, usageBranchLabel } from '../../../../utils/branchShare';
import { togglePassExposure } from '../../../../api/exposure';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputDropdown } from '../../../../form-components/FormInputDropdown';
import { RHFCheckbox } from '../../../CheckBox';
import { order, pageSize, sort, cellStatus } from '../../../../constants/constants';
import { errorMessages, formatterCurrency } from '../../../../utils/methods';
import { centersDetail } from '../../../../api/store-management';
import {
  PageContainer,
  SearchRow,
  SearchInputWrapper,
  SearchTitle,
  SearchButton,
  CreateButton,
  SportFilterRow,
  SportFilterLabel,
  SportFilterContent,
  AllCheckboxWrapper,
  AllCheckboxLabel,
  StyledCheckbox,
  SportFilterDivider,
  CheckboxLabel,
  DirectInputWrapper,
  DirectInputLabel,
  DirectInputField,
  DirectInputUnit,
  ActionButtonRow,
  Divider,
  FilterRow,
  FilterLeftBox,
  FilterRightBox,
  StyledTableContainer,
  StyledTableHead,
  StyledTableRow,
  StyledTableHeaderCell,
  StyledTableHeaderCellLast,
  StyledTableBodyRow,
  StyledTableBodyCell,
  StyledTableBodyCellLast,
  StatusText,
  searchInputStyle,
  sortOrderDropdownStyle,
  sortTypeDropdownStyle,
  limitDropdownStyle,
  Label,
} from './ListPassStyled';
import PlusIcon from '../../../../_icons/PlusIcon';
import CommonPagination from '../../../../form-components/CommonPagination';
import CommonFilterActionButton from '../../../../form-components/CommonFilterActionButton';
import CommonSubscriptionBadge from '../../../../form-components/CommonSubscriptionBadge';
import useUserAuth from '../../../../utils/useUserAuth';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import FavoriteStar from '../../../common/FavoriteStar';
import { FAVORITE_TARGET } from '../../../../api/favorite';
import CommonSearchSubmitButton from '../../../../form-components/CommonSearchSubmitButton';
import PassGuideModal from './PassGuideModal';
import { GuideButton } from '../../../lesson/groupLesson/GroupLessonStyled';

const PERIOD_OPTIONS = [
  { label: '1개월', value: 1 },
  { label: '3개월', value: 3 },
  { label: '6개월', value: 6 },
  { label: '12개월', value: 12 },
];

// 다지점 공유 상품(#98) 지점 컬럼 — 판매/이용 요약 두 줄
const BranchShareText = ({ children }) => (
  <span style={{ display: 'inline-flex', flexDirection: 'column', fontSize: '12px', lineHeight: 1.4, whiteSpace: 'nowrap' }}>
    {children}
  </span>
);

const ListPass = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const navigate = useNavigate();
  const canCreate = useUserAuth('TICKET_PASS_CREATE');

  const initialQuery = {
    page: 1,
    limit: 10,
    passName: '',
    sportTypes: [],
    saleStatuses: [],
    usagePeriods: [],
    sortOrder: 'DESCENDING',
    sortType: 'LATEST',
    branchId: user?.branch?.branchId,
    headBranchId: user?.headBranch?.headBranchId,
  };

  const [query, setQuery] = useState(() => {
    const saved = sessionStorage.getItem('passSearchConditions');
    if (saved) {
      sessionStorage.removeItem('passSearchConditions');
      try { return JSON.parse(saved); } catch { return initialQuery; }
    }
    return initialQuery;
  });
  const [users, setUsers] = useState();
  const [sportType, setSportType] = useState([]);
  const [exposureAlertOpen, setExposureAlertOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [customPeriod, setCustomPeriod] = useState('');
  const [showCustomPeriod, setShowCustomPeriod] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  const { handleSubmit, control, setValue, reset, watch } = useForm({
    defaultValues: initialQuery,
  });

  const watchSportTypes = watch('sportTypes');
  const watchSaleStatuses = watch('saleStatuses');

  const handleChangePage = (_, value) => {
    setQuery({ ...query, page: value });
  };

  const handleChange = (e, key) => {
    const value = e.target.value;
    setValue(key, value);
    setQuery({ ...query, [key]: value });
  };

  const passList = async currentQuery => {
    if (!user.headBranch?.headBranchId || (user.centerType === 'BRANCH' && !user.branch?.branchId)) {
      toast.warning(t('Please select a point'));
      setUsers([]);
      setQuery(initialQuery);
    } else {
      const queryString = qs.stringify(currentQuery, { arrayFormat: 'repeat' });
      try {
        const res = await getAllPass(queryString, user.token);
        setUsers(res);
      } catch (error) {
        toast.error(errorMessages(error));
      }
    }
  };

  // 지점별 sportTypes 조회 (centerId로 조회)
  const centerInfo = async () => {
    try {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      const formattedSportTypes = (response?.branchDetailInfo?.sportTypes || []).map(type => ({
        label: type,
        value: type,
      }));
      setSportType(formattedSportTypes);
    } catch (error) {
      console.log('centerInfo 함수 에러 : ' + error);
    }
  };

  const submit = async values => {
    const newQuery = {
      ...query,
      ...values,
      branchId: user?.branch?.branchId,
      headBranchId: user?.headBranch?.headBranchId,
    };
    setQuery(newQuery);
    passList(newQuery);
  };

  const handleClick = e => {
    if (!user.branch) {
      e.preventDefault();
      toast.warning(t('Please select a point'));
    }
  };

  // 전체 체크박스 핸들러
  const handleAllCheck = fieldName => {
    setValue(fieldName, []);
    setQuery(prev => ({ ...prev, [fieldName]: [] }));
  };

  // 기간 체크박스 핸들러
  const handlePeriodChange = value => {
    const currentPeriods = query.usagePeriods || [];
    const newPeriods = currentPeriods.includes(value)
      ? currentPeriods.filter(v => v !== value)
      : [...currentPeriods, value];
    setQuery({ ...query, usagePeriods: newPeriods });
    setValue('usagePeriods', newPeriods);
  };

  // 직접입력 핸들러
  const handleCustomPeriodChange = e => {
    const value = e.target.value;
    setCustomPeriod(value);
    if (value) {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue) && !query.usagePeriods?.includes(numValue)) {
        setQuery({ ...query, usagePeriods: [...(query.usagePeriods || []), numValue] });
      }
    }
  };

  useEffect(() => {
    setQuery(prevQuery => ({
      ...prevQuery,
      branchId: user?.branch?.branchId,
      headBranchId: user?.headBranch?.headBranchId,
    }));
  }, [user]);

  useEffect(() => {
    passList(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.page, query.limit, query.headBranchId, query.branchId]);

  useEffect(() => {
    localStorage.removeItem('ticket');
  }, []);

  useEffect(() => {
    if (user?.branch?.centerId) {
      centerInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.branch?.centerId]);

  const setInitial = () => {
    const resetQuery = {
      ...initialQuery,
      branchId: user?.branch?.branchId,
      headBranchId: user?.headBranch?.headBranchId,
    };
    setQuery(resetQuery);
    reset(resetQuery);
    setCustomPeriod('');
    setShowCustomPeriod(false);
    passList(resetQuery);
  };

  return (
    <>
    <AnimationPage>
      <div>
        <PageContainer component="div">
          {/* Search Row */}
          <SearchRow>
            <SearchInputWrapper>
              <SearchTitle className="title">{t('Search Pass')}</SearchTitle>
              <FormInputText
                control={control}
                sx={{ width: '370px', mr: '12px' }}
                name="passName"
                placeholder={t('Type the pass title')}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    handleSubmit(submit)();
                  }
                }}
              />
              <CommonSearchSubmitButton onClick={handleSubmit(submit)}>
                {t('Search')}
              </CommonSearchSubmitButton>
            </SearchInputWrapper>
            <Tooltip title={!canCreate ? '권한이 없습니다' : ''} arrow>
              <span>
                <CreateButton
                  {...(canCreate ? { component: Link, to: '/pass/create', onClick: handleClick } : {})}
                  disabled={!canCreate}
                  variant="outlined">
                  <PlusIcon />
                  이용권 생성
                </CreateButton>
              </span>
            </Tooltip>
          </SearchRow>

          {/* 필터 섹션 */}
          <Collapse in={filterOpen}>
          <SportFilterRow>
            <SportFilterLabel>{t('종목')}</SportFilterLabel>
            <SportFilterContent>
              <AllCheckboxWrapper>
                <AllCheckboxLabel
                  control={
                    <StyledCheckbox
                      checked={!watchSportTypes || watchSportTypes.length === 0}
                      onChange={() => handleAllCheck('sportTypes')}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={t('전체')}
                />
              </AllCheckboxWrapper>
              <SportFilterDivider />
              <RHFCheckbox
                control={control}
                row
                name="sportTypes"
                spacing={0}
                options={sportType}
                sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              />
            </SportFilterContent>
          </SportFilterRow>

          <SportFilterRow>
            <SportFilterLabel>{t('판매 상태')}</SportFilterLabel>
            <SportFilterContent>
              <AllCheckboxWrapper>
                <AllCheckboxLabel
                  control={
                    <StyledCheckbox
                      checked={!watchSaleStatuses || watchSaleStatuses.length === 0}
                      onChange={() => handleAllCheck('saleStatuses')}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={t('전체')}
                />
              </AllCheckboxWrapper>
              <SportFilterDivider />
              <RHFCheckbox
                control={control}
                row
                name="saleStatuses"
                spacing={0}
                options={cellStatus}
                sx={{ '& .MuiFormControlLabel-root': { width: '100px', mr: '24px' } }}
              />
            </SportFilterContent>
          </SportFilterRow>

          <SportFilterRow>
            <SportFilterLabel>{t('판매 옵션')}</SportFilterLabel>
            <SportFilterContent>
              <AllCheckboxWrapper>
                <Label>{t('기간')}</Label>
              </AllCheckboxWrapper>
              <SportFilterDivider />
              {PERIOD_OPTIONS.map(option => (
                <CheckboxLabel
                  key={option.value}
                  control={
                    <StyledCheckbox
                      checked={query.usagePeriods?.includes(option.value)}
                      onChange={() => handlePeriodChange(option.value)}
                      icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                      checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                    />
                  }
                  label={option.label}
                />
              ))}
              <CheckboxLabel
                control={
                  <StyledCheckbox
                    checked={showCustomPeriod}
                    onChange={() => setShowCustomPeriod(!showCustomPeriod)}
                    icon={<img src="/images/icons/unchecked.svg" alt="unchecked" />}
                    checkedIcon={<img src="/images/icons/checked.svg" alt="checked" />}
                  />
                }
                label={t('직접 입력')}
              />
              {showCustomPeriod && (
                <DirectInputWrapper>
                  <DirectInputField
                    type="number"
                    value={customPeriod}
                    onChange={handleCustomPeriodChange}
                    placeholder="0"
                  />
                  <DirectInputUnit>{t('개월')}</DirectInputUnit>
                </DirectInputWrapper>
              )}
            </SportFilterContent>
          </SportFilterRow>

          {/* Action Buttons */}
          <ActionButtonRow>
            <CommonFilterActionButton variant="primary" onClick={handleSubmit(submit)}>
              {t('Apply and Search')}
            </CommonFilterActionButton>
            <CommonFilterActionButton variant="secondary" onClick={setInitial}>
              {t('Clear Filters')}
            </CommonFilterActionButton>
          </ActionButtonRow>
          </Collapse>

          <CommonFilterToggle open={filterOpen} onClick={() => setFilterOpen(prev => !prev)} />

          {/* Table Controls */}
          <FilterRow>
            <FilterLeftBox>
              <span>총 {users?.totalElements || 0}건</span>
            </FilterLeftBox>
            <FilterRightBox>
              <GuideButton onClick={() => setGuideOpen(true)} sx={{ mr: 1 }}>📖 이용권 가이드</GuideButton>
              <FormInputDropdown
                name="sortOrder"
                onChange={e => handleChange(e, 'sortOrder')}
                control={control}
                options={order.map(type => ({
                  ...type,
                  label: t(type.label),
                }))}
                sx={sortOrderDropdownStyle}
              />
              <FormInputDropdown
                name="sortType"
                onChange={e => handleChange(e, 'sortType')}
                control={control}
                options={sort.map(type => ({
                  ...type,
                  label: t(type.label),
                }))}
                sx={sortTypeDropdownStyle}
              />
              <FormInputDropdown
                control={control}
                name="limit"
                options={pageSize}
                staticValue={query.limit}
                onChange={e => {
                  const newLimit = e.target.value;
                  setValue('limit', newLimit);
                  setQuery({ ...query, limit: newLimit, page: 1 });
                }}
                sx={limitDropdownStyle}
              />
            </FilterRightBox>
          </FilterRow>

          {/* Table */}
          <StyledTableContainer>
            <Table size="small" aria-label="a dense table">
              <StyledTableHead>
                <StyledTableRow>
                  <StyledTableHeaderCell align="center" sx={{ width: '40px', padding: 0 }} />
                  <StyledTableHeaderCell align="center">{t('Studio')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Sale Type')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Pass Title')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Sports')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Period')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Usage period type')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell align="center">{t('Selling Price')}</StyledTableHeaderCell>
                  {/* <StyledTableHeaderCell align="center">{t('Save Count')}</StyledTableHeaderCell> */}
                  <StyledTableHeaderCell align="center">앱 노출</StyledTableHeaderCell>
                  <StyledTableHeaderCellLast align="center">{t('Sales status')}</StyledTableHeaderCellLast>
                </StyledTableRow>
              </StyledTableHead>
              <TableBody>
                {users?.content?.length === 0 && (
                  <StyledTableBodyRow>
                    <StyledTableBodyCell colSpan={10} align="center" sx={{ padding: '60px 0', color: '#999', fontSize: '14px', borderRight: 'none' }}>
                      검색 결과가 없습니다.
                    </StyledTableBodyCell>
                  </StyledTableBodyRow>
                )}
                {users?.content?.map((ticket, index) => (
                  <StyledTableBodyRow
                    key={index}
                    ended={ticket.saleStatus === 'EXPIRED' || ticket.saleStatus === 'SALE_END'}
                    component={Link}
                    onClick={e => {
                      handleClick(e);
                      if (!user.branch) return;
                      sessionStorage.setItem('passSearchConditions', JSON.stringify(query));
                      navigate(`/pass/${ticket.coursePassId}`);
                    }}
                    to={`/pass/${ticket.coursePassId}`}>
                    {/* 즐겨찾기 — 토글 후 목록을 다시 불러 즐겨찾기 우선 정렬을 맞춘다 */}
                    <StyledTableBodyCell align="center" sx={{ padding: 0 }}>
                      <FavoriteStar
                        targetType={FAVORITE_TARGET.PASS}
                        targetId={ticket.coursePassId}
                        favorite={ticket.favorite}
                        onChanged={() => passList(query)}
                      />
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center" component="th" scope="row">
                      {ticket.branchShare?.shared ? (
                        <BranchShareText>
                          <span>판매 {saleBranchLabel(ticket.branchShare)}</span>
                          <span>이용 {usageBranchLabel(ticket.branchShare)}</span>
                        </BranchShareText>
                      ) : (
                        ticket.storeName ?? ticket.branchName
                      )}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center" component="th" scope="row">
                      {t(ticket?.passType)}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center">
                      {ticket.subscriptionYn && <CommonSubscriptionBadge sx={{ mr: '6px' }} />}
                      {ticket.passName}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center">
                      {ticket.sportTypes?.length
                        ? ticket.sportTypes.map(s => t(s)).join(', ')
                        : t(ticket.sportType)}
                    </StyledTableBodyCell>
                    <StyledTableBodyCell align="center">{ticket.usagePeriod}</StyledTableBodyCell>
                    <StyledTableBodyCell align="center">{t(ticket.usagePeriodType)}</StyledTableBodyCell>
                    <StyledTableBodyCell align="center">{formatterCurrency(ticket.finalPrice)}</StyledTableBodyCell>
                    {/* <StyledTableBodyCell align="center">
                      {ticket.wishListAddedCount ? ticket.wishListAddedCount : '-'}
                    </StyledTableBodyCell> */}
                    <StyledTableBodyCell align="center">
                      <div onClick={e => e.stopPropagation()}>
                      <Switch
                        size="small"
                        checked={ticket.exposureYn === 'Y'}
                        onChange={async e => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (ticket.saleStatus === 'EXPIRED' || ticket.saleStatus === 'SALE_END') {
                            setExposureAlertOpen(true);
                            return;
                          }
                          const newValue = ticket.exposureYn === 'Y' ? 'N' : 'Y';
                          try {
                            await togglePassExposure(user.token, ticket.coursePassId, newValue);
                            setUsers(prev => ({
                              ...prev,
                              content: prev.content.map(t => t.coursePassId === ticket.coursePassId ? { ...t, exposureYn: newValue } : t),
                            }));
                            toast.success(newValue === 'Y' ? '앱에 노출됩니다' : '앱에서 숨겨집니다');
                          } catch { toast.error('변경에 실패했습니다'); }
                        }}
                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4AB3BC' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4AB3BC' } }}
                      />
                      </div>
                    </StyledTableBodyCell>
                    <StyledTableBodyCellLast align="center">
                      <StatusText status={ticket.saleStatus}>{t(`TICKET_${ticket.saleStatus}`)}</StatusText>
                    </StyledTableBodyCellLast>
                  </StyledTableBodyRow>
                ))}
              </TableBody>
            </Table>
          </StyledTableContainer>

          {/* Pagination */}
          <CommonPagination
            totalElements={users?.totalElements}
            limit={query.limit}
            page={query.page}
            onChange={handleChangePage}
          />
        </PageContainer>
      </div>
    </AnimationPage>

    <ConfirmModal
      open={exposureAlertOpen}
      onClose={() => setExposureAlertOpen(false)}
      onConfirm={() => setExposureAlertOpen(false)}
      message={`판매종료된 상품은\n앱 노출이 불가합니다.`}
      highlightText="__none__"
      highlightColor="#4AB3BC"
      cancelText="닫기"
      confirmText="확인"
      confirmColor="#4AB3BC"
    />

    <PassGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </>
  );
};

export default ListPass;
